import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import dayjs from 'dayjs';

import { useLanguage } from 'assets/languages';
import { ROUTES } from 'routes';
import API from 'api';
import { queries } from 'api/api.constants';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import { CampaignManagerDto } from 'api/repositories/campaignManager/campaignManager.types';
import {
  UpdateCampaignCampaignManagersDto,
  UpdateCampaignRequestParams,
  UpdateCampaignResponse,
} from 'api/repositories/campaign/campaign.types';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { PaymentProductType } from 'types/payment';
import { isKoreaService } from 'constants/domains/domains';
import { COUNTRY_CD } from 'constants/country';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';
import { debounce } from 'helpers/debounce';
import OverlayBrandTaxInvoiceInfoForm, {
  OverlayBrandTaxInvoiceInfoFormResult,
} from 'components/overlayBrandTaxInvoiceInfoForm';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { GetParsedCampaignParams } from 'pages/campaigns/campaignForm/campaignForm.types';
import PaymentCreateBeauty from 'pages/payments/createBeauty';
import PaymentComplete from 'pages/payments/complete';

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

const RETURN_MANAGER_FIELD_NAMES = [
  'returnManagerName',
  'returnManagerMobile',
  'returnManagerCountryCode',
  'returnAddress',
] as GetParsedCampaignParams['fieldNames'];

interface StoreOrUpdateCampaignProps {
  campaignManagers: UpdateCampaignCampaignManagersDto;
  onSuccessCallback: () => void;
}

type OnClickNextStepProps = {
  validateCampaign: () => boolean;
  onSuccessStoreOrUpdateTemporaryCampaignCallback: () => void;
};

interface CampaignManagerFormProps {
  managers: Pick<CampaignManagerDto, 'name' | 'countryCode' | 'mobile' | 'position'>[];
}

interface CampaignFormStepThreeContextProps {
  campaignManagerFormMethods: UseFormReturn<CampaignManagerFormProps>;
  countryCodeOptions: { name: string; value: string }[];
  seedingPassEndsAt: dayjs.Dayjs | null;
  isSeedingPassActive: boolean;
  isFormValid: boolean;
  createCampaign: () => void;
  proceedPayment: (paymentProductType: PaymentProductType) => void;
  onClickNextStep: (props: OnClickNextStepProps) => void;
}

const initialContext: CampaignFormStepThreeContextProps = {
  campaignManagerFormMethods: null,
  countryCodeOptions: [],
  seedingPassEndsAt: null,
  isSeedingPassActive: false,
  isFormValid: false,
  createCampaign: () => {},
  proceedPayment: () => {},
  onClickNextStep: () => {},
};

export const CampaignFormStepThreeContext = createContext<CampaignFormStepThreeContextProps>(initialContext);

export const CampaignFormStepThreeProvider = ({ children }: { children: ReactNode }) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    modal,
    auth,
    brand,
    refetchBrand,
    setLegacyBrand,
    products,
    temporaryKey,
    campaignFormMethods,
    campaignPaymentFormMethods,
    updateCampaignId,
    getGuidelineRulesParams,
    getParsedCampaign,
    getParsedGuidelineRules,
    prepareStoreCampaignFormData,
    storeOrUpdateTemporaryCampaignMutate,
    showLoader,
    setShowLoader,
    initializeCampaignFormData,
  } = useContext(CampaignFormContext);

  const countryCodeOptions = [
    { name: `${languageTexts.country_code_empty}`, value: '' },
    { name: `${languageTexts.korea} (+${COUNTRY_CD.KR})`, value: COUNTRY_CD.KR },
    { name: `${languageTexts.japan} (+${COUNTRY_CD.JP})`, value: COUNTRY_CD.JP },
  ];

  const campaignManagerFormMethods = useForm<CampaignManagerFormProps>();

  const isProcessingPayment = useRef(false);

  // 브랜드 담당자 정보
  const campaignManagerFormValues = useWatch({
    control: campaignManagerFormMethods.control,
    name: 'managers',
  });

  const returnManagerFormValues = useWatch({
    control: campaignFormMethods.control,
    name: RETURN_MANAGER_FIELD_NAMES,
  });

  const inspectionSubmitDueDt = useWatch({
    control: campaignFormMethods.control,
    name: 'inspectionSubmitDueDt',
  });

  const inspectionFeedbackDueDt = useWatch({
    control: campaignFormMethods.control,
    name: 'inspectionFeedbackDueDt',
  });

  const priceCommission = useWatch({
    control: campaignPaymentFormMethods.control,
    name: 'priceCommission',
  });

  // 브랜드 담당자 정보 조회
  const { data: campaignManagers, refetch: refetchCampaignManagers } = useQuery({
    ...queries.campaignManager.getCampaignManagers({
      brandToken: auth.token,
      campaignId: updateCampaignId ? updateCampaignId : null,
      temporaryKey: updateCampaignId ? null : temporaryKey,
    }),
    enabled: !!auth.token,
    select: (data) => data.data,
    staleTime: 1000 * 60 * 5, // 5분
    gcTime: 1000 * 60 * 5, // 5분
  });

  // 브랜드 담당자 정보 조회 성공 후 처리
  useEffect(() => {
    if (campaignManagers?.length > 0) {
      campaignManagerFormMethods.reset({ managers: campaignManagers });
    } else if (brand?.members?.length > 0) {
      campaignManagerFormMethods.reset({
        managers: brand.members.map((member) => ({
          name: member?.name || '',
          countryCode: member?.countryCode || '',
          mobile: member?.mobile
            ? (() => {
                // 하이픈과 공백 제거
                const cleaned = member.mobile.replace(/[-\s]/g, '');
                // 숫자만 있는지 확인
                return /^[0-9]+$/.test(cleaned) ? cleaned : '';
              })()
            : '',
        })),
      });
    } else if (
      campaignFormMethods.getValues('brandManagerName') && 
      campaignFormMethods.getValues('brandManagerMobile') && 
      campaignFormMethods.getValues('brandManagerCountryCode')) {
      campaignManagerFormMethods.reset({
        managers: [
          {
            name: campaignFormMethods.getValues('brandManagerName'),
            countryCode: campaignFormMethods.getValues('brandManagerCountryCode'),
            mobile: campaignFormMethods.getValues('brandManagerMobile'),
          },
        ],
      });
    }
  }, [campaignManagers]);

  const seedingPassEndsAt = useMemo(() => {
    return brand?.activeSeedingPassEndsAt ? dayjs(brand.activeSeedingPassEndsAt) : null;
  }, [brand?.activeSeedingPassEndsAt]);

  const isSeedingPassActive = useMemo(() => {
    const postingEndsAt = campaignFormMethods.getValues('postingEndsAt');

    const isActiveInPeriod =
      postingEndsAt &&
      seedingPassEndsAt &&
      (seedingPassEndsAt.isSame(postingEndsAt) || seedingPassEndsAt.isAfter(postingEndsAt));

    const expectedToBeActiveInPeriod = brand?.hasSeedingPass && brand?.isSeedingPassAutoPurchaseEnabled === 1;

    return isActiveInPeriod || expectedToBeActiveInPeriod;
  }, [brand?.isSeedingPassAutoPurchaseEnabled, seedingPassEndsAt]);

  const isFormValid = useMemo(() => {
    if (
      // 브랜드 담당자 입력이 안된 경우
      !campaignManagerFormValues ||
      campaignManagerFormValues?.length === 0 ||
      // 브랜드 담당자 정보가 모두 입력되지 않은 경우
      campaignManagerFormValues?.some(
        (value) => !value.name || !value.countryCode || !value.mobile || !value.position,
      ) ||
      // 반품 담당자 정보가 모두 입력되지 않은 경우
      returnManagerFormValues?.some((value) => !value)
    ) {
      return false;
    } else {
      return true;
    }
  }, [campaignManagerFormValues, returnManagerFormValues]);

  // 캠페인 생성
  const { mutateAsync: createCampaignMutateAsync } = useMutation({
    mutationFn: () =>
      API.Campaign.create({
        temporaryKey,
        brandToken: auth.token,
        brandBusinessType: brand.businessType,
        campaign: prepareStoreCampaignFormData(campaignFormMethods.getValues()),
        guidelineRules: getParsedGuidelineRules(),
        campaignManagers: campaignManagerFormMethods.getValues('managers'),
        paymentData: campaignPaymentFormMethods.getValues(),
      }),
    onSuccess: (response) => {
      navigate(ROUTES.CAMPAIGN_COMPLETE.PATH, {
        state: { campaign: response.data.campaign, isUpdate: false },
        replace: true,
      });
    },
    onError: (error) => {
      modal.showModal({ text: error.message });
    },
    onSettled: () => {
      setShowLoader(false);
    },
  });

  // 캠페인 수정
  const { mutate: updateCampaignMutate, isPending: isUpdateCampaignPending } = useMutation({
    mutationFn: (data: UpdateCampaignRequestParams) => API.Campaign.updateCampaign(data),
  });

  const createCampaign = useCallback(async () => {
    if (!showLoader) {
      setShowLoader(true);
      await createCampaignMutateAsync();
    }
  }, []);

  const proceedPayment = useCallback((paymentProductType: PaymentProductType) => {
    if (!isProcessingPayment.current) {
      modal.closeOverlays();

      let amount = products.reduce((acc, product) => acc + product.qty, 0);
      let price = brand.priceSeeding;
      let priceTotal = brand.priceSeeding * amount;

      switch (paymentProductType) {
        case PAYMENT_PRODUCT_TYPE.SEEDING_PASS:
        case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_BEAUTY_YEARLY:
          price = brand.priceSeedingPass;
          priceTotal = brand.priceSeedingPass;
          break;
        case PAYMENT_PRODUCT_TYPE.SEEDING_PASS_RECOVER:
          amount = 1;
          price = brand.priceSeedingPass;
          priceTotal = null;
          break;
      }

      debounce(async () => {
        // 캠페인 최종 결제금액 = 서비스 이용료 + 원고료 + 사전검수비용
        const hasServiceFeeToPay = brand.isCategoryBeauty || !isSeedingPassActive;
        const hasCommissionToPay =
          campaignFormMethods.getValues('commissionAmount') != null &&
          campaignFormMethods.getValues('commissionAmount') > 0;
        const hasInspectionFeeToPay = campaignFormMethods.getValues('inspectionSubmitDueDt') != null;

        if (!hasServiceFeeToPay && !hasCommissionToPay && !hasInspectionFeeToPay) {
          // 결제가 필요없는 경우 캠페인 생성만 진행
          createCampaign();
        } else {
          // 결제가 필요한 경우 결제창으로 이동

          // 현금 결제 브랜드의 경우 세금계산서 정보 입력
          let cashCampaignPaymentData = null;
          let brandQueryRes = await refetchBrand(); // 현금/카드 상태 최신화를 위해 강제 갱신 (운영 쪽에서 중간에 바꾸는 경우 많다고 함)
          setLegacyBrand(brandQueryRes.data);

          if (isKoreaService() && brandQueryRes.data.billingType === BRAND_DTO_ENUMS.BILLING_TYPE.FREE) {
            const brandTaxInvoiceInfoFormResult = await new Promise<OverlayBrandTaxInvoiceInfoFormResult>(
              (resolve, reject) => {
                modal.showOverlays({
                  content: () => <OverlayBrandTaxInvoiceInfoForm onComplete={(result) => resolve(result)} />,
                  onRequestClose: () => {
                    resolve({
                      isSubmitted: false,
                      data: {
                        brand: {
                          businessName: null,
                          businessCeoName: null,
                          businessNumber: null,
                          businessEmail: null,
                        },
                        campaignPayment: {
                          taxInvoiceScheduledDate: null,
                          depositScheduledDate: null,
                        }
                      }
                    });
                  },
                });
              },
            );
            if (!brandTaxInvoiceInfoFormResult.isSubmitted) {
              setShowLoader(false);
              return;
            }
            cashCampaignPaymentData = brandTaxInvoiceInfoFormResult.data.campaignPayment;
          }

          // 무제한 요금제 등 상태 최신화
          brandQueryRes = await refetchBrand();
          setLegacyBrand(brandQueryRes.data);

          setShowLoader(false);
          modal.showOverlays({
            content: () => {
              return (
                <Elements stripe={stripePromise}>
                  <PaymentCreateBeauty
                    temporaryKey={temporaryKey}
                    itemType={paymentProductType}
                    amount={amount}
                    price={price}
                    priceTotal={priceTotal}
                    priceCommission={priceCommission ?? null}
                    cashCampaignPaymentData={cashCampaignPaymentData}
                    // handleChange={(target, value) => {
                    //   // commission_amount 변경하는 경우
                    //   campaignFormMethods.setValue(target, value);
                    // }} // DEPRECATED/250527
                    onSuccess={async (response) => {
                      await createCampaign();

                      setTimeout(() => {
                        modal.showOverlays({
                          content: () => {
                            return (
                              <PaymentComplete
                                itemType={response.itemType}
                                onConfirm={() => {
                                  modal.closeOverlays();
                                }}
                              />
                            );
                          },
                        });
                      }, 400);
                    }}
                  />
                </Elements>
              );
            },
          });
        }
      }, 400);
    }
  }, []);

  const storeOrUpdateCampaign = useCallback(
    ({ onSuccessCallback, campaignManagers }: StoreOrUpdateCampaignProps) => {
      setShowLoader(true);

      // 업데이트인 경우
      if (updateCampaignId) {
        updateCampaignMutate(
          {
            isMaster: auth.isMaster,
            brandToken: auth.token,
            userTypeFl: GUIDELINE_RULE_DTO_ENUMS.USER_TYPE_FL.CAMPAIGN_NORMAL,
            campaign: campaignFormMethods.getValues(),
            guidelineRules: getParsedGuidelineRules(),
            campaignManagers,
          },
          {
            onSuccess: async (data: UpdateCampaignResponse) => {
              // guidelineRule Query invalidate
              await queryClient.resetQueries({
                queryKey: queries.guidelineRules.getGuidelineRules(getGuidelineRulesParams).queryKey,
              });
              // 브랜드 담당자 정보 Query invalidate
              refetchCampaignManagers();
              navigate(ROUTES.CAMPAIGN_COMPLETE.PATH, {
                state: { campaign: data.data.campaign, isUpdate: true },
                replace: true,
              });
            },
            onError: (error) => {
              modal.showAlertModal({
                title: languageTexts.error,
                text: (error as Error)?.message,
              });
            },
            onSettled: () => {
              setShowLoader(false);
            },
          },
        );
      } else {
        const returnManagers = getParsedCampaign({
          fieldNames: RETURN_MANAGER_FIELD_NAMES,
        });

        storeOrUpdateTemporaryCampaignMutate({
          campaign: {
            ...returnManagers,
          },
          campaignManagers,
          options: {
            onSuccess: (response) => {
              // 업데이트된 campaign 데이터로 formData 초기화
              initializeCampaignFormData({
                loadedCampaign: response.campaign,
              });
              // guidelineRule Query invalidate
              queryClient.resetQueries({
                queryKey: queries.guidelineRules.getGuidelineRules(getGuidelineRulesParams).queryKey,
              });
              // 브랜드 담당자 정보 Query invalidate
              refetchCampaignManagers();

              onSuccessCallback();
            },
          },
        });
      }
    },
    [auth, updateCampaignId, showLoader, isUpdateCampaignPending],
  );

  const onClickNextStep = useCallback((params: OnClickNextStepProps) => {
      const isValidCampaign = params.validateCampaign();
      if (!isValidCampaign) {
        return;
      }

      if (!isUpdateCampaignPending && !showLoader) {
        const campaignManagers = campaignManagerFormMethods
          .getValues('managers')
          .filter((manager) => manager.name && manager.mobile && manager.countryCode);

        if (inspectionFeedbackDueDt != null) {
          modal.showModal({
            title: languageTexts.create_step3_has_inspection_due_dt_modal_tile,
            text: (
              <div className="flex flex-col gap-[30px]">
                <ul className="flex flex-col gap-[10px]">
                  {campaignManagers.map(({ name, countryCode, mobile, position }, index) => (
                    <li key={`has-inspection-due-dt-model-manager-${index}`}>
                      {name} {position} (+{countryCode}) {mobile}
                    </li>
                  ))}
                </ul>
                <span className="text-[#FF0000] text-[15px]">
                  {languageTexts.create_step3_has_inspection_due_dt_modal_notice}
                </span>
              </div>
            ),
            hideCloseButton: true,
            showCancelButton: true,
            cancelButtonText: languageTexts.create_step3_has_inspection_due_dt_modal_cancel_button_text,
            confirmButtonText: languageTexts.create_step3_has_inspection_due_dt_modal_confirm_button_text,
            confirm: () => {
              storeOrUpdateCampaign({
                onSuccessCallback: params.onSuccessStoreOrUpdateTemporaryCampaignCallback,
                campaignManagers,
              });
            },
          });
        } else {
          storeOrUpdateCampaign({
            onSuccessCallback: params.onSuccessStoreOrUpdateTemporaryCampaignCallback,
            campaignManagers,
          });
        }
      }
    },
    [inspectionFeedbackDueDt, storeOrUpdateCampaign],
  );

  return (
    <CampaignFormStepThreeContext.Provider
      value={{
        campaignManagerFormMethods,
        countryCodeOptions,
        seedingPassEndsAt,
        isSeedingPassActive,
        isFormValid,
        createCampaign,
        proceedPayment,
        onClickNextStep,
      }}
    >
      {children}
    </CampaignFormStepThreeContext.Provider>
  );
};
